import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import toolkitService from '../services/ToolkitService';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitFileStep, { STEP, stepIcon } from './toolkit/ToolkitFileStep';
import ToolkitBatchTable from './toolkit/ToolkitBatchTable';
import ToolkitMappingEditor from './toolkit/ToolkitMappingEditor';
import useStoredRows from './toolkit/useStoredRows';
import { sourceRow } from './toolkit/useToolkitBatch';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › Event Management — ported from the desktop's Scripts › Event Management. The events of a
// v5 Event List Detailed report are created in Opera Cloud on their blocks (toolkit groups.js process-event):
//
//   1. Code mappings — v5 → Opera Cloud event types, function spaces, setups and the rest, per property; a code with
//      no mapping is sent as it is. Seeding gives the desktop's identity defaults to start from.
//   2. The event report, and the v5 Reservation Block Info report that gives each event's block its CODE.
//   3. Look up the Opera Cloud block for each code.
//   4. Process — an event already created by an earlier run is updated, never created twice.

const CATEGORIES = [
    ['EVENT_TYPE', 'Event types'], ['BOOKING_STATUS', 'Booking status'], ['BOOKING_TYPE', 'Booking types'],
    ['SC_FUNCTION_SPACE', 'Function spaces'], ['FUNCTION_SPACE_LOCATION', 'Space locations'],
    ['FUNCTION_SPACE_RATE_TYPE', 'Space rate types'], ['MEETING_ROOMTYPE', 'Meeting room types'],
    ['SC_FUNCTION_SPACE_RATE_CODE', 'Space rate codes'], ['SC_FUNCTION_SPACE_SETUP', 'Setups'], ['SC_REVENUE_TYPES', 'Revenue types'],
].map(([key, label]) => ({ key, label }));
// The desktop's defaults, from the OXI conversion code export (identity mappings to start from).
const SEED = {
    EVENT_TYPE: ['ACC', 'AFT', 'ATC', 'BFT', 'BRU', 'CLOA', 'COM', 'DEL', 'DIN', 'ENT', 'INT', 'LUN', 'MID', 'MTG', 'OTH', 'PART', 'PCO', 'PDD', 'PPT', 'SCREENING', 'SET', 'SETD', 'SHO', 'SYN', 'TRA'],
    BOOKING_STATUS: ['ACT', 'ALL', 'CON', 'CXL', 'INQ', 'LST', 'PRO', 'REF', 'TEN', 'WAIT'],
    BOOKING_TYPE: ['BAN', 'INH', 'MTG', 'WED', 'XMA'],
    SC_FUNCTION_SPACE: ['110', '120', '130', '140', 'ALL', 'APAR', 'BED', 'CIN', 'CLUB', 'CLUB7', 'CLUB7+8', 'CLUBAS', 'CLUBR', 'COU', 'COW', 'COWSHED', 'DES', 'FRO', 'HOU', 'KIT', 'L110', 'L120', 'L130', 'L140', 'LBA', 'LEN', 'LOB', 'LTER', 'OUT', 'PILA', 'POBAR', 'POL', 'POLFOY', 'POLKIT', 'RAWL', 'RAWM', 'RAWS', 'RED', 'RES', 'ROOF', 'ROOF8', 'ROOFB', 'SIT', 'SPE', 'STO', 'STORE', 'STUDIO', 'TER', 'TOR', 'TOR1', 'TOR19', 'TOR2', 'XL39'],
    FUNCTION_SPACE_LOCATION: ['MAIN'],
    FUNCTION_SPACE_RATE_TYPE: ['PP', 'PR'],
    MEETING_ROOMTYPE: ['CAT', 'CON', 'F&B', 'GRO'],
    SC_FUNCTION_SPACE_RATE_CODE: ['COM', 'CUSTOM', 'FUL', 'FULM', 'HALF', 'HALFM', 'INC'],
    SC_FUNCTION_SPACE_SETUP: ['BAR', 'BOA', 'CAB', 'CIR', 'CLA', 'CLI', 'CLO', 'EMP', 'EXI', 'HAL', 'LONG', 'LOU', 'ONE', 'REC', 'ROU', 'SEEN', 'SETUP', 'STA', 'THE', 'TOBE', 'TWO', 'USH'],
    SC_REVENUE_TYPES: ['ACC', 'AV', 'BEV', 'F&BC', 'FOOD', 'MISC', 'OTHER', 'RENTAL'],
};

const COLUMNS = [
    { key: 'eventDate', label: 'Date' },
    { key: 'startTime', label: 'Time', render: (r) => (r.startTime ? `${r.startTime}–${r.endTime || ''}` : '') },
    { key: 'eventName', label: 'Event' },
    { key: 'eventType', label: 'Type' },
    { key: 'blockName', label: 'Block' },
    { key: 'sourceBlockCode', label: 'Block code' },
    { key: 'targetBlockId', label: 'Cloud block' },
    { key: 'functionSpaceRoom', label: 'Space' },
    { key: 'targetEventId', label: 'Cloud event' },
];

function ToolkitEventManagementPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const [mappings, setMappings] = useState({});
    const [blocksByV5Id, setBlocksByV5Id] = useState({});
    const [lookup, setLookup] = useState({ state: STEP.IDLE, message: '' });

    const run = useMemo(() => ({
        request: (row) => ({ area: 'groups', op: 'process-event', args: { row: sourceRow(row, ['steps']), mappings } }),
        server: { screen: 'event-mgmt', title: 'Event Management', keyOf: (row) => row.rowId },
    }), [mappings]);
    const { batch, replace, saveRow, message } = useStoredRows(ctx, ready, 'event-mgmt', run);

    // The block report only supplies codes: merged onto the event rows (and saved) when either file is loaded.
    const withCodes = (rows, byId) => rows.map((r) => ({ ...r, sourceBlockCode: r.sourceBlockCode || byId[r.sourceBlockId]?.sourceBlockCode || '' }));
    const onEvents = (rows) => replace(withCodes(rows, blocksByV5Id));
    const onBlocks = (blocks) => {
        const byId = Object.fromEntries(blocks.filter((b) => b.sourceBlockId).map((b) => [b.sourceBlockId, b]));
        setBlocksByV5Id(byId);
        if (batch.rows.length) replace(withCodes(batch.rows, byId));
    };

    const lookupBlocks = async () => {
        const codes = [...new Set(batch.rows.filter((r) => !r.targetBlockId && r.sourceBlockCode).map((r) => r.sourceBlockCode))];
        if (!codes.length) { setLookup({ state: STEP.DONE, message: 'Every event with a block code already has its Opera Cloud block.' }); return; }
        setLookup({ state: STEP.BUSY, message: `Looking up ${codes.length} block code(s)…` });
        try {
            const { blocks } = (await toolkitService.groups(ctx, 'lookup-event-blocks', { codes })).data.result;
            let updated = 0;
            for (let i = 0; i < batch.rows.length; i++) {
                const r = batch.rows[i];
                const hit = !r.targetBlockId && blocks[r.sourceBlockCode]?.blockId;
                if (hit) { const row = batch.patch(i, { targetBlockId: hit }); await saveRow(row); updated++; }
            }
            const missing = Object.entries(blocks).filter(([, v]) => !v.blockId).map(([k]) => k);
            setLookup({ state: missing.length ? STEP.ERROR : STEP.DONE,
                message: `${codes.length - missing.length} of ${codes.length} block(s) found; ${updated} event(s) updated.`
                    + (missing.length ? ` Not found: ${missing.slice(0, 10).join(', ')}${missing.length > 10 ? '…' : ''}` : '') });
        } catch (err) {
            setLookup({ state: STEP.ERROR, message: err?.response?.data?.message || err.message });
        }
    };

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="Event Management"
                subtitle="Create Opera Cloud events from an Opera v5 Event List Detailed report, on their blocks." />

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">1</span> Code mappings (v5 → Opera Cloud)</div>
                <ToolkitMappingEditor ctx={ctx} ready={ready} configKey="event-mgmt-code-mappings" categories={CATEGORIES} seed={SEED} onChange={setMappings} />
            </div>

            <ToolkitFileStep number={2} title="Opera v5 Event List Detailed" label="Choose the event report (XML)" accept=".xml,.rep"
                kind="event-list" onParsed={onEvents} disabled={!ready}>
                {message && <p className="tkb-msg">{message}</p>}
            </ToolkitFileStep>
            <ToolkitFileStep number={3} title="Opera v5 Reservation Block Info (block codes)" label="Choose resblkinfo (XML)" accept=".xml,.rep"
                kind="event-blocks" onParsed={onBlocks} disabled={!ready} />

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">4</span> Opera Cloud blocks {stepIcon(lookup.state)}</div>
                <Button variant="secondary" onClick={lookupBlocks} disabled={!ready || !batch.rows.length || batch.running || lookup.state === STEP.BUSY}>
                    <Search className="icon" /> Look up blocks
                </Button>
                {lookup.message && <p className={`tkb-msg${lookup.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{lookup.message}</p>}
            </div>

            <ToolkitBatchTable batch={batch} columns={COLUMNS} searchKeys={['eventName', 'blockName', 'sourceBlockCode', 'eventType']}
                exportName="Event_Management" canRun={ready} onClear={() => replace([])} />
        </div>
    );
}

export default ToolkitEventManagementPage;
